import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CartTimeoutDialog } from '@/components/shared/Dialog';
import { parseUtcTimestamp } from '@/lib/parseUtcTimestamp';

interface CartTimeoutWarningProps {
    timesOutAtUtc: string;
    slug: string;
}

export function CartTimeoutWarning({ timesOutAtUtc, slug }: CartTimeoutWarningProps) {
    const [timeLeft, setTimeLeft] = useState<{ minutes: number; seconds: number }>({ minutes: 0, seconds: 0 });
    const [showTimeoutDialog, setShowTimeoutDialog] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Parsed by hand — not `new Date()` — because older api versions send this without an
        // offset, which `new Date()` would read as the visitor's local time; see
        // parseUtcTimestamp (FAS-1376).
        const timeoutDate = parseUtcTimestamp(timesOutAtUtc);

        // An unparseable deadline is "no countdown", never "timed out" (that would clear the cart).
        if (Number.isNaN(timeoutDate)) {
            return;
        }

        const calculateTimeLeft = () => {
            const utcNow = Date.now();
            const difference = timeoutDate - utcNow;
            
            if (difference <= 0) {
                return { minutes: 0, seconds: 0 };
            }

            const minutesLeft = Math.floor(difference / 1000 / 60);
            const secondsLeft = Math.floor((difference / 1000) % 60);

            return { minutes: minutesLeft, seconds: secondsLeft };
        };

        // Initial calculation
        const initialTimeLeft = calculateTimeLeft();
        setTimeLeft(initialTimeLeft);

        if (initialTimeLeft.minutes === 0 && initialTimeLeft.seconds === 0) {
            handleTimeout();
        }

        // Update every second
        const timer = setInterval(() => {
            const newTimeLeft = calculateTimeLeft();
            setTimeLeft(newTimeLeft);
            
            if (newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
                handleTimeout();
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [timesOutAtUtc]);

    const handleTimeout = () => {
        const cartKey = `marketplace_cart_id_${slug}`;
        localStorage.removeItem(cartKey);
        setShowTimeoutDialog(true);
    };

    const handleTimeoutDialogClose = () => {
        setShowTimeoutDialog(false);
        navigate({ 
            to: '/marketplace/$slug', 
            params: { slug },
            search: { listing: undefined, email: undefined }
        });
        window.location.reload();
    };

    if (!timesOutAtUtc) {
        return null;
    }

    const formatTime = () => {
        if (timeLeft.minutes > 2) {
            return `${timeLeft.minutes} minutes`;
        }
        return `${timeLeft.minutes}:${timeLeft.seconds.toString().padStart(2, '0')}`;
    };

    return (
        <>
            <CartTimeoutDialog isOpen={showTimeoutDialog} onClose={handleTimeoutDialogClose} />
            {timeLeft.minutes > 0 || timeLeft.seconds > 0 ? (
                <div className="mt-4 flex justify-end">
                    <p className="text-sm">
                        Time left to complete checkout: <span className="font-medium font-mono">{formatTime()}</span>
                    </p>
                </div>
            ) : null}
        </>
    );
} 